/**
 * ProgressTracker — real-time progress display.
 * Timer is NOT managed here — it lives at the Processing page level.
 */

import React, { useState } from 'react';
import { CATEGORY_COLORS, CATEGORY_LABELS, PhoneCategory, JobStatusResponse, ProgressUpdate, CallLogEntry } from '../types';
import { CallLogPanel } from './CallLogPanel';

interface Props {
  status: JobStatusResponse | null;
  progress: ProgressUpdate | null;
  isComplete: boolean;
  callLog: CallLogEntry[];
  inFlightCalls: Map<string, CallLogEntry>;
}

export const ProgressTracker: React.FC<Props> = ({ status, progress, isComplete, callLog, inFlightCalls }) => {
  const [showLog, setShowLog] = useState(false);

  const total   = progress?.total   ?? status?.total_numbers   ?? 0;
  // Drive the bar from the most-advanced LIVE signal so it tracks the result chips and
  // never freezes on the lagging polled processed_count. callLog (WS call_completed
  // events) updates in real time; progress/processed_count are fallbacks and also cover
  // big jobs where callLog is capped at 200 entries.
  const completedFromLog = callLog.reduce((n, e) => n + (e.type === 'call_completed' ? 1 : 0), 0);
  const liveCurrent = Math.max(progress?.current ?? 0, status?.processed_count ?? 0, completedFromLog);
  const current = total > 0 ? Math.min(liveCurrent, total) : liveCurrent;
  const pct     = total > 0 ? Math.round((current / total) * 100) : 0;
  const counts  = progress?.category_counts ?? status?.category_counts ?? {};

  return (
    <div style={{ padding: 24 }}>
      {/* Top row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-strong)' }}>
          {isComplete ? '✅ Done' : '🔄 Processing...'}
        </span>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ color: 'var(--text-soft)', fontSize: 13 }}>
            {current} of {total} ({pct}%)
          </span>

          {/* ⓘ live log button */}
          <button
            onClick={() => setShowLog(true)}
            title="View live call log"
            style={{
              position: 'relative',
              width: 30, height: 30, borderRadius: '50%',
              border: '1.5px solid var(--border)',
              background: 'var(--status-analysis-processing-bg)', color: 'var(--status-analysis-processing-fg)',
              cursor: 'pointer', fontSize: 14, fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            ⓘ
            {callLog.length > 0 && (
              <span style={{
                position: 'absolute', top: -4, right: -4,
                background: 'var(--status-analysis-failed-fg)', color: '#fff',
                borderRadius: '50%', width: 15, height: 15,
                fontSize: 9, fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {callLog.length > 99 ? '99+' : callLog.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ background: 'var(--status-neutral-bg)', borderRadius: 8, height: 12, overflow: 'hidden', marginBottom: 16 }}>
        <div
          style={{
            height: '100%',
            width: `${pct}%`,
            background: isComplete
              ? 'var(--green)'
              : 'linear-gradient(90deg, var(--teal), var(--amber))',
            transition: 'width 0.5s ease',
            borderRadius: 8,
          }}
        />
      </div>

      {/* Currently dialing */}
      {progress?.current_number && !isComplete && (
        <p style={{ fontSize: 12, color: 'var(--text-soft)', marginBottom: 12 }}>
          Dialing:{' '}
          <code style={{ background: 'var(--surface-subtle)', padding: '1px 6px', borderRadius: 4 }}>
            {progress.current_number}
          </code>
          {progress.current_status && (
            <span style={{
              marginLeft: 8, padding: '2px 8px', borderRadius: 10, fontSize: 11,
              background: CATEGORY_COLORS[progress.current_status as PhoneCategory] || 'var(--status-neutral-bg)',
            }}>
              {CATEGORY_LABELS[progress.current_status as PhoneCategory] || progress.current_status}
            </span>
          )}
        </p>
      )}

      {/* AMD in-flight indicator */}
      {inFlightCalls.size > 0 && !isComplete && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          marginBottom: 12, padding: '6px 12px',
          background: 'var(--status-analysis-processing-bg)', borderRadius: 8, fontSize: 12,
        }}>
          <span style={{
            width: 8, height: 8, borderRadius: '50%',
            background: 'var(--status-analysis-processing-fg)', display: 'inline-block',
            animation: 'pulse-dot 1.2s ease-in-out infinite',
          }} />
          <span style={{ color: 'var(--status-analysis-processing-fg)', fontWeight: 600 }}>
            {inFlightCalls.size} call{inFlightCalls.size !== 1 ? 's' : ''} waiting for AMD response
          </span>
          <button
            onClick={() => setShowLog(true)}
            style={{
              marginLeft: 'auto', fontSize: 11, color: 'var(--status-analysis-processing-fg)',
              background: 'none', border: 'none', cursor: 'pointer',
              textDecoration: 'underline', padding: 0,
            }}
          >
            View details
          </button>
        </div>
      )}

      {/* Category breakdown */}
      {Object.keys(counts).length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {Object.entries(counts).map(([cat, count]) => (
            <span
              key={cat}
              style={{
                padding: '4px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                background: CATEGORY_COLORS[cat as PhoneCategory] || 'var(--status-neutral-bg)',
              }}
            >
              {CATEGORY_LABELS[cat as PhoneCategory] || cat}: {count}
            </span>
          ))}
        </div>
      )}

      {status?.error_message && (
        <p style={{ color: 'var(--status-analysis-failed-fg)', marginTop: 12 }}>Error: {status.error_message}</p>
      )}

      {/* Live log drawer */}
      {showLog && (
        <CallLogPanel
          onClose={() => setShowLog(false)}
          callLog={callLog}
          inFlightCalls={inFlightCalls}
          progress={progress ? {
            current: progress.current,
            total: progress.total,
            category_counts: progress.category_counts,
          } : null}
        />
      )}

      <style>{`
        @keyframes pulse-dot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(0.8); }
        }
      `}</style>
    </div>
  );
};
